/**
 * Ads Automation Agent — admin tab (Fase A).
 *
 * Read-only + draft-only. NO Google Ads mutations. NO budget changes.
 * Approval queue items never auto-apply (autonomy level 1 lock).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Loader2,
  RefreshCw, Eye, BookOpen, ScrollText, Gauge, Brain,
  Wrench, Search, TrendingDown,
} from 'lucide-react';

const API = '/api';

function authHeaders(token) {
  return { headers: { Cookie: `access_token=${token}` }, withCredentials: true };
}

const COLOR_BADGE = {
  green: 'bg-green-100 text-green-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
};

function Banner() {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="ads-agent-autonomy-banner">
      <ShieldCheck className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-amber-900 space-y-1">
        <p><strong>Autonomy Level 1 — Draft Only.</strong> Geen Google Ads mutaties. Geen budgetwijziging. Geen live publicatie. Approval-first.</p>
        <p>Voorstellen komen in approval queue als <code className="bg-amber-100 px-1 rounded">pending</code>. Approvals worden gelogd maar niet automatisch uitgevoerd (`apply_status: blocked_by_autonomy_level_1`).</p>
      </div>
    </div>
  );
}

function StatusPanel({ status, onRefresh }) {
  if (!status) return null;
  const cfg = status.config || {};
  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm space-y-3" data-testid="ads-agent-status-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Gauge className="w-4 h-4 text-primary" /> Setup / Readiness
        </h3>
        <button onClick={onRefresh} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-primary" data-testid="ads-agent-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="text-xs flex gap-4 flex-wrap">
        <span><strong>Autonomy:</strong> Level {cfg.autonomy_level} ({cfg.mode})</span>
        <span><strong>Mutations:</strong> {cfg.mutation_enabled ? <span className="text-red-700">ENABLED</span> : <span className="text-green-700">DISABLED (Phase A)</span>}</span>
        <span><strong>Configured:</strong> {cfg.configured ? <span className="text-green-700">true</span> : <span className="text-amber-700">false</span>}</span>
        <span><strong>Conversion env:</strong> {cfg.frontend_conversion_env_present ? <span className="text-green-700">present</span> : <span className="text-amber-700">missing</span>}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {(cfg.env_vars || []).map(v => (
          <div key={v.name} className="text-[11px] flex items-center justify-between bg-muted/30 rounded px-2 py-1">
            <span className="font-mono">{v.name}</span>
            <span className={v.status === 'missing' ? 'text-amber-700' : 'text-green-700'}>
              {v.status === 'missing' ? 'missing' : (v.masked_value || 'configured')}
            </span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border text-xs">
        {status.counts?.approval_queue && Object.entries(status.counts.approval_queue).map(([k, v]) => (
          <div key={k} className="text-center">
            <div className="text-[10px] text-muted-foreground capitalize">{k}</div>
            <div className="text-base font-semibold">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MeasurementPanel({ measurement }) {
  if (!measurement) return null;
  const color = COLOR_BADGE[measurement.color] || COLOR_BADGE.amber;
  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm" data-testid="ads-agent-measurement-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Brain className="w-4 h-4 text-primary" /> Meetkwaliteit (gate voor budget-advies)
        </h3>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${color}`}>
          {measurement.color?.toUpperCase()}
        </span>
      </div>
      <div className="space-y-1">
        {(measurement.checks || []).map((c, i) => (
          <div key={i} className="text-[11px] flex items-center gap-2">
            {c.ok ? <CheckCircle2 className="w-3 h-3 text-green-600" /> : <XCircle className="w-3 h-3 text-red-600" />}
            <span className="font-mono">{c.name}</span>
            <span className="text-muted-foreground">— {c.detail}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px]">
        Scale-up allowed: <strong>{String(measurement.scale_up_allowed)}</strong>
        {!measurement.scale_up_allowed && measurement.scale_up_blocker_reason && (
          <span className="text-muted-foreground italic"> — {measurement.scale_up_blocker_reason}</span>
        )}
      </div>
    </div>
  );
}

function RecommendationsPanel({ data, token, onChange }) {
  const [busyIdx, setBusyIdx] = useState(null);
  if (!data) return null;

  const promote = async (idx) => {
    setBusyIdx(idx);
    try {
      await axios.post(`${API}/admin/ads-agent/approval-queue/from-recommendation`,
        { recommendation_index: idx, days: data.window_days }, authHeaders(token));
      toast.success('Voorstel naar approval queue gepushed (pending).');
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Promote failed');
    }
    setBusyIdx(null);
  };

  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm space-y-3" data-testid="ads-agent-recommendations-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Eye className="w-4 h-4 text-primary" /> Recommendations (laatste {data.window_days}d)
        </h3>
        <span className="text-[11px] text-muted-foreground">{data.recommendation_count} suggesties</span>
      </div>
      <div className="text-[11px] text-muted-foreground">
        <strong>Funnel counts:</strong>{' '}
        {Object.entries(data.funnel_counts || {}).map(([k, v]) => `${k}=${v}`).join(' · ')}
      </div>
      {data.data_sufficiency?.notes?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900">
          <AlertTriangle className="w-3 h-3 inline mr-1" />
          {data.data_sufficiency.notes.join(' ')}
        </div>
      )}
      {(data.recommendations || []).length === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center">
          Geen recommendations — meetwindow / volume nog te beperkt.
        </div>
      ) : (
        <div className="space-y-2">
          {data.recommendations.map((r, i) => (
            <div key={i} className="border border-border rounded p-2 space-y-1" data-testid={`ads-agent-rec-${i}`}>
              <div className="flex items-center justify-between flex-wrap gap-1">
                <span className="text-xs font-medium">
                  P{r.priority} · {r.title}
                </span>
                <button
                  onClick={() => promote(i)}
                  disabled={busyIdx === i}
                  className="px-2 py-1 text-[11px] bg-primary text-white rounded hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
                  data-testid={`ads-agent-rec-promote-${i}`}
                >
                  {busyIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : '→'} Naar approval queue
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">{r.reason}</p>
              <p className="text-[11px]">
                <strong>Risk:</strong> {r.risk} · <strong>Impact:</strong> {r.expected_impact}
              </p>
              <p className="text-[11px] italic text-muted-foreground">→ {r.action_suggested}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApprovalQueuePanel({ items, token, onChange }) {
  const [busy, setBusy] = useState(null);
  const act = async (id, op) => {
    setBusy(id + op);
    try {
      await axios.post(`${API}/admin/ads-agent/approval-queue/${id}/${op}`,
        { reason: op === 'reject' ? 'Niet relevant' : '' }, authHeaders(token));
      toast.success(`${op} ok`);
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${op} failed`);
    }
    setBusy(null);
  };
  const validate = async (id) => {
    setBusy(id + 'validate');
    try {
      const r = await axios.post(`${API}/admin/ads-agent/approval-queue/${id}/validate`, {}, authHeaders(token));
      const blocked = r.data?.blocked;
      if (blocked) toast.warning(`validate: ${blocked}`);
      else toast.success(`validate: ${r.data?.validation_status || 'done'}`);
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'validate failed');
    }
    setBusy(null);
  };
  const apply = async (id) => {
    if (!window.confirm('Na apply worden deze negatives LIVE in Google Ads toegevoegd. Budgetten en campagnes worden NIET gewijzigd. Doorgaan?')) return;
    setBusy(id + 'apply');
    try {
      const r = await axios.post(`${API}/admin/ads-agent/approval-queue/${id}/apply`,
        { confirm_apply: true }, authHeaders(token));
      const blocked = r.data?.blocked;
      if (blocked) toast.warning(`apply: ${blocked}`);
      else toast.success(`apply: ${r.data?.apply_status || 'done'}`);
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'apply failed');
    }
    setBusy(null);
  };

  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm" data-testid="ads-agent-approval-queue">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <ScrollText className="w-4 h-4 text-primary" /> Approval Queue ({items.length})
      </h3>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center">
          Leeg. Promote eerst een recommendation om approval-flow te testen.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(it => (
            <div key={it.id} className="border border-border rounded p-2 space-y-1" data-testid={`ads-agent-queue-${it.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-1">
                <span className="text-xs font-medium">P{it.priority} · {it.action_type} · {it.title}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${COLOR_BADGE[it.admin_status === 'approved' ? 'green' : it.admin_status === 'rejected' ? 'red' : 'amber']}`}>
                  {it.admin_status}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">{it.reason}</p>
              <p className="text-[10px] font-mono text-muted-foreground">apply_status: {it.apply_status}{it.validation_status ? ` · validation: ${it.validation_status}` : ''}{it.apply_request_id ? ` · req_id: ${it.apply_request_id}` : ''}</p>
              {it.admin_status === 'pending' && (
                <div className="flex gap-1">
                  <button
                    onClick={() => act(it.id, 'approve')}
                    disabled={busy === it.id + 'approve'}
                    className="px-2 py-0.5 text-[11px] bg-green-600 text-white rounded disabled:opacity-50"
                    data-testid={`ads-agent-approve-${it.id}`}
                  >Approve</button>
                  <button
                    onClick={() => act(it.id, 'reject')}
                    disabled={busy === it.id + 'reject'}
                    className="px-2 py-0.5 text-[11px] bg-red-600 text-white rounded disabled:opacity-50"
                    data-testid={`ads-agent-reject-${it.id}`}
                  >Reject</button>
                </div>
              )}
              {it.admin_status === 'approved' && it.action_type === 'add_negative_keyword' && (
                <div className="flex gap-1 flex-wrap">
                  <button
                    onClick={() => validate(it.id)}
                    disabled={busy === it.id + 'validate'}
                    className="px-2 py-0.5 text-[11px] bg-blue-600 text-white rounded disabled:opacity-50"
                    data-testid={`ads-agent-validate-${it.id}`}
                  >Validate (dry-run)</button>
                  <button
                    onClick={() => apply(it.id)}
                    disabled={busy === it.id + 'apply' || it.validation_status !== 'validated'}
                    className="px-2 py-0.5 text-[11px] bg-amber-600 text-white rounded disabled:opacity-50"
                    data-testid={`ads-agent-apply-${it.id}`}
                    title={it.validation_status === 'validated' ? 'Klaar voor apply' : 'Eerst Validate'}
                  >Apply</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LearningLogPanel({ items }) {
  return (
    <details className="bg-white rounded-xl border border-border p-4 shadow-sm" data-testid="ads-agent-learning-log">
      <summary className="text-sm font-semibold cursor-pointer flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-primary" /> Learning Log ({items.length})
      </summary>
      <div className="mt-3 space-y-1 text-[11px]">
        {items.length === 0 ? (
          <div className="text-muted-foreground py-2 text-center">Nog geen learning entries.</div>
        ) : items.map((e, i) => (
          <div key={i} className="bg-muted/30 rounded p-2">
            <div><strong>{e.decision || e.kind || '?'}</strong> · {e.action_type} · {e.recommendation_text || e.recommendation_id}</div>
            {e.decision_reason && <div className="italic text-muted-foreground">→ {e.decision_reason}</div>}
            {e.observed_result && <div className="italic text-muted-foreground">obs: {e.observed_result}</div>}
            <div className="text-[10px] text-muted-foreground">{e.created_at}</div>
          </div>
        ))}
      </div>
    </details>
  );
}

export default function AdsAutomationAgentTab({ token }) {
  const [status, setStatus] = useState(null);
  const [recs, setRecs] = useState(null);
  const [queue, setQueue] = useState([]);
  const [learn, setLearn] = useState([]);
  const [funnelProblems, setFunnelProblems] = useState(null);
  const [searchTerms, setSearchTerms] = useState(null);
  const [troubleshoot, setTroubleshoot] = useState(null);
  const [adsCreds, setAdsCreds] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, q, l, fp, tc, ac] = await Promise.all([
        axios.get(`${API}/admin/ads-agent/status`, authHeaders(token)),
        axios.get(`${API}/admin/ads-agent/recommendations?days=30`, authHeaders(token)),
        axios.get(`${API}/admin/ads-agent/approval-queue?limit=50`, authHeaders(token)),
        axios.get(`${API}/admin/ads-agent/learning-log?limit=50`, authHeaders(token)),
        axios.get(`${API}/admin/ads-agent/analyze/funnel-problems?days=2`, authHeaders(token)),
        axios.get(`${API}/admin/ads-agent/troubleshooting/checks`, authHeaders(token)),
        axios.get(`${API}/admin/ads-agent/google-ads/credentials`, authHeaders(token)),
      ]);
      setStatus(s.data);
      setRecs(r.data);
      setQueue(q.data.items || []);
      setLearn(l.data.items || []);
      setFunnelProblems(fp.data);
      setTroubleshoot(tc.data);
      setAdsCreds(ac.data);
      if (ac.data?.configured) {
        try {
          const st = await axios.get(
            `${API}/admin/ads-agent/google-ads/search-terms?date_range=LAST_7_DAYS`,
            authHeaders(token),
          );
          setSearchTerms(st.data);
        } catch (_err) {
          // search-terms fail tolerated — UI shows "could not fetch"
          setSearchTerms({ ok: false, error: 'fetch_failed' });
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Ads Agent');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  if (loading && !status) {
    return <div className="flex items-center justify-center py-8 text-muted-foreground">
      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Laden…
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="ads-agent-tab">
      <Banner />
      <StatusPanel status={status} onRefresh={reload} />
      <MeasurementPanel measurement={recs?.measurement_quality} />
      <FunnelProblemsPanel data={funnelProblems} />
      <AdsCredentialsPanel creds={adsCreds} />
      <SearchTermsPanel data={searchTerms} configured={adsCreds?.configured} />
      <RecommendationsPanel data={recs} token={token} onChange={reload} />
      <ApprovalQueuePanel items={queue} token={token} onChange={reload} />
      <TroubleshootingPanel data={troubleshoot} />
      <LearningLogPanel items={learn} />
    </div>
  );
}

// ---- New panels (Phase B read-only) ----

function FunnelProblemsPanel({ data }) {
  if (!data) return null;
  const problems = data.problems || [];
  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm space-y-2" data-testid="ads-agent-funnel-problems-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-primary" /> Funnel Problems (48u)
        </h3>
        <span className="text-xs text-muted-foreground">
          window: {data.window_days}d · spend: {data.ads_spend_eur != null ? `€${data.ads_spend_eur.toFixed(2)}` : 'n.v.t.'}
        </span>
      </div>
      <div className="text-xs grid grid-cols-2 md:grid-cols-5 gap-1 text-muted-foreground">
        {['page_view','quickscan_started','quickscan_completed','demo_viewed','demo_cta_clicked','payment_clicked','checkout_started','payment_completed'].map(k => (
          <div key={k} className="bg-muted/30 px-2 py-1 rounded">
            {k.replace(/_/g,' ')}: <strong>{data.funnel_counts?.[k] ?? 0}</strong>
          </div>
        ))}
      </div>
      {problems.length === 0 ? (
        <p className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Geen funnelproblemen gedetecteerd (op huidige drempels).</p>
      ) : problems.map((p, i) => (
        <div key={i} className={`border rounded p-2 text-xs space-y-1 ${p.severity==='critical'?'border-red-300 bg-red-50':p.severity==='high'?'border-amber-300 bg-amber-50':'border-border bg-muted/20'}`} data-testid={`funnel-problem-${p.type}`}>
          <div className="flex items-center justify-between">
            <strong className="capitalize">{p.type.replace(/_/g,' ')}</strong>
            <span className={`text-[10px] uppercase font-bold ${p.severity==='critical'?'text-red-700':p.severity==='high'?'text-amber-700':'text-muted-foreground'}`}>{p.severity}</span>
          </div>
          <p className="text-muted-foreground">{p.proposed_action}</p>
          <p className="text-[11px] text-muted-foreground">Metric: <code>{p.metric_to_watch}</code> · Impact: {p.expected_impact} · Risk: {p.risk}</p>
        </div>
      ))}
    </div>
  );
}

function AdsCredentialsPanel({ creds }) {
  if (!creds) return null;
  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm space-y-2" data-testid="ads-agent-credentials-panel">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-primary" /> Google Ads API — Credentials
      </h3>
      {creds.configured ? (
        <p className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Geconfigureerd · customer_id set: {creds.customer_id_set ? 'ja' : 'nee'}</p>
      ) : (
        <div className="text-xs text-amber-800 space-y-1">
          <p className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Ontbreekt: {(creds.missing || []).join(', ')}</p>
          <p className="text-muted-foreground">Voeg toe in <code className="bg-muted px-1 rounded">backend/.env</code> en restart backend. Vereist read-only developer token.</p>
        </div>
      )}
    </div>
  );
}

function SearchTermsPanel({ data, configured }) {
  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm space-y-2" data-testid="ads-agent-search-terms-panel">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <Search className="w-4 h-4 text-primary" /> Search Terms — Intent Classifier
      </h3>
      {!configured ? (
        <p className="text-xs text-muted-foreground">Beschikbaar zodra Google Ads API geconfigureerd is.</p>
      ) : !data?.ok ? (
        <p className="text-xs text-red-600">Kon search terms niet ophalen: {data?.error || 'unknown'}.</p>
      ) : (
        <>
          <div className="text-xs grid grid-cols-4 gap-1 text-muted-foreground">
            {Object.entries(data.summary_by_action || {}).map(([k, v]) => (
              <div key={k} className="bg-muted/30 px-2 py-1 rounded">{k}: <strong>{v}</strong></div>
            ))}
          </div>
          <div className="max-h-64 overflow-auto border rounded">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 sticky top-0">
                <tr><th className="text-left p-1">Search term</th><th className="text-left p-1">Cost</th><th className="text-left p-1">Clicks</th><th className="text-left p-1">Conv.</th><th className="text-left p-1">Intent</th><th className="text-left p-1">Action</th></tr>
              </thead>
              <tbody>
                {(data.rows || []).slice(0, 50).map((r, i) => (
                  <tr key={i} className="border-t" data-testid={`search-term-row-${i}`}>
                    <td className="p-1 truncate max-w-[180px]" title={r.search_term}>{r.search_term}</td>
                    <td className="p-1">€{((r.cost_micros||0)/1_000_000).toFixed(2)}</td>
                    <td className="p-1">{r.clicks}</td>
                    <td className="p-1">{r.conversions}</td>
                    <td className="p-1">
                      <span className={`px-1 rounded text-[10px] ${r.intent?.label==='high_intent'?'bg-green-100 text-green-700':r.intent?.label==='medium_intent'?'bg-amber-100 text-amber-700':r.intent?.label==='conflict'?'bg-red-100 text-red-700':'bg-muted text-muted-foreground'}`}>{r.intent?.label}</span>
                    </td>
                    <td className="p-1 font-medium">{r.intent?.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function TroubleshootingPanel({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white rounded-xl border border-border p-4 shadow-sm space-y-2" data-testid="ads-agent-troubleshooting-panel">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <Wrench className="w-4 h-4 text-primary" /> Troubleshooting checks ({(data.checks || []).length})
      </h3>
      <p className="text-[11px] text-muted-foreground">Volledige playbook: <code className="bg-muted px-1 rounded">{data.playbook_path}</code></p>
      <div className="grid md:grid-cols-2 gap-2">
        {(data.checks || []).map(c => (
          <div key={c.id} className="border rounded p-2 text-xs space-y-1" data-testid={`troubleshoot-${c.id}`}>
            <strong>{c.title}</strong>
            <p className="text-muted-foreground">{c.description}</p>
            <p className="text-[11px]"><strong>Fix:</strong> {c.fix_hint}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
